/**
 * useBuyAgain — pulls the customer's recent orders and surfaces the most
 * frequently re-ordered products (or just the most recent, fallback) so
 * we can render the "Order again" rail on home.
 *
 * Returns up to `limit` product ids paired with the source store id (so we
 * can re-add via the cart API).
 */
import { useEffect, useState } from 'react';
import { ordersAPI, OrderStatus } from '../api/orders';
import { useAuthStore } from '../stores/authStore';

export interface BuyAgainHit {
  product_id: number;
  store_id: number;
  /** Snapshot at order time (for "X · ₹400" hover). */
  last_price: number;
  /** Total times this product appears in last 50 orders. */
  count: number;
  last_ordered_at: string;
  /** Server may snapshot the product name on the order item. */
  name?: string;
}

export function useBuyAgain(limit: number = 12) {
  const isAuthed = useAuthStore((s) => s.isAuthenticated);
  const role = useAuthStore((s) => s.user?.role);
  const [hits, setHits] = useState<BuyAgainHit[]>([]);
  const [loading, setLoading] = useState(isAuthed && role === 'customer');

  useEffect(() => {
    if (!isAuthed || role !== 'customer') {
      setHits([]); setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const orders = await ordersAPI.listOrders(0, 50);
        const counts: Map<number, BuyAgainHit> = new Map();
        for (const order of orders) {
          // Only look at successful orders, not cancelled or pending payment.
          if (order.status === OrderStatus.CANCELLED || order.status === OrderStatus.REJECTED) continue;
          for (const it of order.items || []) {
            if (!it.product_id) continue;
            const ex = counts.get(it.product_id);
            const name = (it as any).item_name as string | undefined;
            if (ex) {
              ex.count += it.quantity;
              if (new Date(it.created_at) > new Date(ex.last_ordered_at)) {
                ex.last_ordered_at = it.created_at;
                ex.last_price = it.price_at_order;
                if (name) ex.name = name;
              }
            } else {
              counts.set(it.product_id, {
                product_id: it.product_id,
                store_id: order.store_id,
                count: it.quantity,
                last_price: it.price_at_order,
                last_ordered_at: it.created_at,
                name,
              });
            }
          }
        }
        if (cancelled) return;
        const sorted = Array.from(counts.values())
          .sort((a, b) => (b.count - a.count) || (new Date(b.last_ordered_at).getTime() - new Date(a.last_ordered_at).getTime()))
          .slice(0, limit);
        setHits(sorted);
      } catch {
        setHits([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthed, role, limit]);

  return { hits, loading };
}

export default useBuyAgain;

